import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, SaveIcon, CheckCircleIcon, Loader2Icon, XIcon } from 'lucide-react';
type DesignVariant = 'standard' | 'minimal' | 'compact' | 'dark-navy' | 'dark-elegant' | 'modern-dark' | 'gradient' | 'glassmorphism' | 'deep-ocean' | 'navy-gradient' | 'deep-ocean-blue';
interface BottomNavigationProps {
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  onFinalize: () => void;
  canProceed: boolean;
  quoteStatus: 'draft' | 'in-progress' | 'ready' | 'completed';
  isSavingDraft: boolean;
  isEditMode?: boolean;
  designVariant?: DesignVariant;
  onCancel?: () => void;
}
export function BottomNavigation({
  currentStep,
  totalSteps,
  onBack,
  onNext,
  onSaveDraft,
  onFinalize,
  canProceed,
  quoteStatus,
  isSavingDraft,
  isEditMode = false,
  designVariant = 'standard',
  onCancel
}: BottomNavigationProps) {
  const isLastStep = currentStep === totalSteps - 1;
  // Standard Design
  if (designVariant === 'standard') {
    return <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg" data-id="element-17">
        <div className="max-w-6xl mx-auto px-6 py-4" data-id="element-18">
          <div className="mb-4" data-id="element-19">
            <div className="flex items-center justify-between mb-2" data-id="element-20">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide" data-id="element-21">
                Paso {currentStep + 1} de {totalSteps}
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-22">
              <div className={`h-full transition-all duration-300 ease-out ${isLastStep ? 'bg-blue-800' : 'bg-blue-600'}`} style={{
              width: `${(currentStep + 1) / totalSteps * 100}%`
            }} data-id="element-23"></div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4" data-id="element-24">
            <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-25">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-26" />
              Anterior
            </button>
            <div className="flex items-center gap-3" data-id="element-27">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors" data-id="element-28">
                {isSavingDraft ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-29" />
                    Guardando...
                  </> : <>
                    <SaveIcon className="w-4 h-4" data-id="element-30" />
                    Guardar Borrador
                  </>}
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-blue-800 rounded-lg hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg" data-id="element-31">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-32" />
                  Finalizar Cotización
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-blue-600 bg-white border-2 border-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-33">
                  Continuar
                  <ChevronRightIcon className="w-4 h-4" data-id="element-34" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Minimal Design
  if (designVariant === 'minimal') {
    return <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200" data-id="element-35">
        <div className="max-w-6xl mx-auto px-6 py-3" data-id="element-36">
          <div className="flex items-center justify-between" data-id="element-37">
            <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-38">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-39" />
              Anterior
            </button>
            <span className="text-xs text-gray-500" data-id="element-40">
              {currentStep + 1} / {totalSteps}
            </span>
            <div className="flex items-center gap-2" data-id="element-41">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors" data-id="element-42">
                {isSavingDraft ? 'Guardando...' : 'Guardar'}
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-43">
                  Finalizar
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-44">
                  Siguiente
                  <ChevronRightIcon className="w-4 h-4" data-id="element-45" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Compact Design
  if (designVariant === 'compact') {
    return <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow" data-id="element-46">
        <div className="max-w-6xl mx-auto px-6 py-2" data-id="element-47">
          <div className="flex items-center justify-between gap-4" data-id="element-48">
            <div className="flex items-center gap-3" data-id="element-49">
              <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="p-2 text-gray-700 hover:bg-gray-100 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-50">
                <ChevronLeftIcon className="w-4 h-4" data-id="element-51" />
              </button>
              <div className="flex items-center gap-2" data-id="element-52">
                <div className="w-24 h-1 bg-gray-200 rounded-full overflow-hidden" data-id="element-53">
                  <div className="h-full bg-blue-600 transition-all duration-300" style={{
                  width: `${(currentStep + 1) / totalSteps * 100}%`
                }} data-id="element-54"></div>
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap" data-id="element-55">
                  {currentStep + 1}/{totalSteps}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-56">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="p-2 text-gray-700 hover:bg-gray-100 rounded disabled:opacity-60 disabled:cursor-not-allowed transition-colors" title="Guardar Borrador" data-id="element-57">
                {isSavingDraft ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-58" /> : <SaveIcon className="w-4 h-4" data-id="element-59" />}
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-60">
                  <CheckCircleIcon className="w-4 h-4" data-id="element-61" />
                  Finalizar
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="p-2 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-62">
                  <ChevronRightIcon className="w-4 h-4" data-id="element-63" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Dark Navy Design
  if (designVariant === 'dark-navy') {
    return <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-950 to-blue-900 border-t-2 border-blue-700 shadow-2xl" data-id="element-64">
        <div className="max-w-6xl mx-auto px-6 py-4" data-id="element-65">
          <div className="mb-4" data-id="element-66">
            <div className="flex items-center justify-between mb-2" data-id="element-67">
              <span className="text-xs font-semibold text-blue-300 uppercase tracking-wide" data-id="element-68">
                Paso {currentStep + 1} de {totalSteps}
              </span>
            </div>
            <div className="w-full h-2 bg-blue-950 rounded-full overflow-hidden border border-blue-800" data-id="element-69">
              <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-300 ease-out shadow-lg shadow-blue-500/50" style={{
              width: `${(currentStep + 1) / totalSteps * 100}%`
            }} data-id="element-70"></div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4" data-id="element-71">
            <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-blue-100 bg-blue-900 border border-blue-700 rounded-lg hover:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all" data-id="element-72">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-73" />
              Anterior
            </button>
            <div className="flex items-center gap-3" data-id="element-74">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-blue-100 bg-blue-900 border border-blue-700 rounded-lg hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all" data-id="element-75">
                {isSavingDraft ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-76" />
                    Guardando...
                  </> : <>
                    <SaveIcon className="w-4 h-4" data-id="element-77" />
                    Guardar Borrador
                  </>}
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg hover:from-cyan-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/30" data-id="element-78">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-79" />
                  Finalizar Cotización
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg hover:from-cyan-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/30" data-id="element-80">
                  Continuar
                  <ChevronRightIcon className="w-4 h-4" data-id="element-81" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Dark Elegant Design
  if (designVariant === 'dark-elegant') {
    return <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-2xl" data-id="element-82">
        <div className="max-w-6xl mx-auto px-6 py-4" data-id="element-83">
          <div className="mb-3" data-id="element-84">
            <div className="flex items-center justify-between mb-3" data-id="element-85">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-widest" data-id="element-86">
                Paso {currentStep + 1} de {totalSteps}
              </span>
              <span className="text-xs text-slate-500" data-id="element-87">
                {Math.round((currentStep + 1) / totalSteps * 100)}% Completado
              </span>
            </div>
            <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden" data-id="element-88">
              <div className="h-full bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400 transition-all duration-500 ease-out" style={{
              width: `${(currentStep + 1) / totalSteps * 100}%`
            }} data-id="element-89"></div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4" data-id="element-90">
            <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-300 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all border border-slate-700" data-id="element-91">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-92" />
              Anterior
            </button>
            <div className="flex items-center gap-3" data-id="element-93">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all" data-id="element-94">
                {isSavingDraft ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-95" />
                    Guardando...
                  </> : <>
                    <SaveIcon className="w-4 h-4" data-id="element-96" />
                    Guardar
                  </>}
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-600/30" data-id="element-97">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-98" />
                  Finalizar
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-600/30" data-id="element-99">
                  Continuar
                  <ChevronRightIcon className="w-4 h-4" data-id="element-100" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Modern Dark Design
  if (designVariant === 'modern-dark') {
    return <div className="fixed bottom-0 left-0 right-0 bg-gray-950 border-t-2 border-indigo-900/50 shadow-2xl backdrop-blur-sm" data-id="element-101">
        <div className="max-w-6xl mx-auto px-6 py-5" data-id="element-102">
          <div className="flex items-center justify-between gap-6" data-id="element-103">
            <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-300 bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all border border-gray-800" data-id="element-104">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-105" />
              <span className="hidden sm:inline" data-id="element-106">Anterior</span>
            </button>
            <div className="flex-1 flex items-center gap-4" data-id="element-107">
              <div className="flex-1 max-w-md" data-id="element-108">
                <div className="flex items-center justify-between mb-2" data-id="element-109">
                  <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider" data-id="element-110">
                    Paso {currentStep + 1}
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-111">
                    {totalSteps} pasos
                  </span>
                </div>
                <div className="relative h-2 bg-gray-900 rounded-full overflow-hidden border border-gray-800" data-id="element-112">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 transition-all duration-500" style={{
                  width: `${(currentStep + 1) / totalSteps * 100}%`
                }} data-id="element-113"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 opacity-50 blur-sm transition-all duration-500" style={{
                  width: `${(currentStep + 1) / totalSteps * 100}%`
                }} data-id="element-114"></div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-115">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-300 bg-gray-900 border border-gray-800 rounded-xl hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all" data-id="element-116">
                {isSavingDraft ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-117" /> : <SaveIcon className="w-4 h-4" data-id="element-118" />}
                <span className="hidden sm:inline" data-id="element-119">
                  {isSavingDraft ? 'Guardando...' : 'Guardar'}
                </span>
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-indigo-600/30" data-id="element-120">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-121" />
                  Finalizar
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-indigo-600/30" data-id="element-122">
                  <span className="hidden sm:inline" data-id="element-123">Continuar</span>
                  <ChevronRightIcon className="w-4 h-4" data-id="element-124" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Gradient Design
  if (designVariant === 'gradient') {
    return <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 shadow-2xl" data-id="element-125">
        <div className="max-w-6xl mx-auto px-6 py-4" data-id="element-126">
          <div className="mb-4" data-id="element-127">
            <div className="flex items-center justify-between mb-2" data-id="element-128">
              <span className="text-xs font-bold text-white/90 uppercase tracking-widest" data-id="element-129">
                Paso {currentStep + 1} de {totalSteps}
              </span>
              <span className="text-xs font-semibold text-white/80 bg-white/20 px-3 py-1 rounded-full" data-id="element-130">
                {Math.round((currentStep + 1) / totalSteps * 100)}%
              </span>
            </div>
            <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm" data-id="element-131">
              <div className="h-full bg-white shadow-lg shadow-white/50 transition-all duration-500 ease-out" style={{
              width: `${(currentStep + 1) / totalSteps * 100}%`
            }} data-id="element-132"></div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4" data-id="element-133">
            <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-white/20 backdrop-blur-md rounded-lg hover:bg-white/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all border border-white/30" data-id="element-134">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-135" />
              Anterior
            </button>
            <div className="flex items-center gap-3" data-id="element-136">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-white/20 backdrop-blur-md border border-white/30 rounded-lg hover:bg-white/30 disabled:opacity-60 disabled:cursor-not-allowed transition-all" data-id="element-137">
                {isSavingDraft ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-138" />
                    Guardando...
                  </> : <>
                    <SaveIcon className="w-4 h-4" data-id="element-139" />
                    Guardar
                  </>}
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-purple-700 bg-white rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xl" data-id="element-140">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-141" />
                  Finalizar Cotización
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-purple-700 bg-white rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xl" data-id="element-142">
                  Continuar
                  <ChevronRightIcon className="w-4 h-4" data-id="element-143" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Glassmorphism Design
  if (designVariant === 'glassmorphism') {
    return <div className="fixed bottom-0 left-0 right-0 bg-white/70 backdrop-blur-xl border-t border-white/50 shadow-2xl" data-id="element-144">
        <div className="max-w-6xl mx-auto px-6 py-4" data-id="element-145">
          <div className="mb-4" data-id="element-146">
            <div className="flex items-center justify-between mb-2" data-id="element-147">
              <span className="text-xs font-bold text-cyan-700 uppercase tracking-widest" data-id="element-148">
                Paso {currentStep + 1} de {totalSteps}
              </span>
            </div>
            <div className="w-full h-2 bg-cyan-100/50 rounded-full overflow-hidden backdrop-blur-sm border border-cyan-200/50" data-id="element-149">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-all duration-500 ease-out shadow-lg shadow-cyan-500/50" style={{
              width: `${(currentStep + 1) / totalSteps * 100}%`
            }} data-id="element-150"></div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4" data-id="element-151">
            <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-cyan-700 bg-white/60 backdrop-blur-md rounded-xl hover:bg-white/80 disabled:opacity-40 disabled:cursor-not-allowed transition-all border border-cyan-200/50 shadow-lg" data-id="element-152">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-153" />
              Anterior
            </button>
            <div className="flex items-center gap-3" data-id="element-154">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-cyan-700 bg-white/60 backdrop-blur-md border border-cyan-200/50 rounded-xl hover:bg-white/80 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg" data-id="element-155">
                {isSavingDraft ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-156" />
                    Guardando...
                  </> : <>
                    <SaveIcon className="w-4 h-4" data-id="element-157" />
                    Guardar Borrador
                  </>}
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl hover:from-cyan-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xl shadow-cyan-500/30" data-id="element-158">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-159" />
                  Finalizar Cotización
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl hover:from-cyan-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xl shadow-cyan-500/30" data-id="element-160">
                  Continuar
                  <ChevronRightIcon className="w-4 h-4" data-id="element-161" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Deep Ocean Design
  if (designVariant === 'deep-ocean') {
    return <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-[#0a1e3d] via-[#0d3d5c] to-[#1a7a8a] border-t-2 border-teal-600/30 shadow-2xl" data-id="element-162">
        <div className="max-w-6xl mx-auto px-6 py-4" data-id="element-163">
          <div className="mb-3" data-id="element-164">
            <div className="flex items-center justify-between mb-3" data-id="element-165">
              <span className="text-xs font-bold text-teal-300 uppercase tracking-widest" data-id="element-166">
                Paso {currentStep + 1} de {totalSteps}
              </span>
              <span className="text-xs font-semibold text-teal-200 bg-[#0a1e3d]/50 px-3 py-1 rounded-full border border-teal-600/30" data-id="element-167">
                {Math.round((currentStep + 1) / totalSteps * 100)}% Completado
              </span>
            </div>
            <div className="w-full h-2 bg-[#0a1e3d]/50 rounded-full overflow-hidden border border-teal-600/30" data-id="element-168">
              <div className="h-full bg-gradient-to-r from-teal-400 to-teal-300 transition-all duration-500 ease-out shadow-lg shadow-teal-400/50" style={{
              width: `${(currentStep + 1) / totalSteps * 100}%`
            }} data-id="element-169"></div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4" data-id="element-170">
            <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-teal-100 bg-[#0a1e3d]/50 border border-teal-600/30 rounded-lg hover:bg-[#0a1e3d]/70 disabled:opacity-40 disabled:cursor-not-allowed transition-all" data-id="element-171">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-172" />
              Anterior
            </button>
            <div className="flex items-center gap-3" data-id="element-173">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-teal-100 bg-[#0a1e3d]/50 border border-teal-600/30 rounded-lg hover:bg-[#0a1e3d]/70 disabled:opacity-60 disabled:cursor-not-allowed transition-all" data-id="element-174">
                {isSavingDraft ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-175" />
                    Guardando...
                  </> : <>
                    <SaveIcon className="w-4 h-4" data-id="element-176" />
                    Guardar
                  </>}
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-[#0a1e3d] bg-teal-400 rounded-lg hover:bg-teal-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-teal-400/30" data-id="element-177">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-178" />
                  Finalizar Cotización
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-[#0a1e3d] bg-teal-400 rounded-lg hover:bg-teal-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-teal-400/30" data-id="element-179">
                  Continuar
                  <ChevronRightIcon className="w-4 h-4" data-id="element-180" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Deep Ocean Blue Design
  if (designVariant === 'deep-ocean-blue') {
    return <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-[#0a1e3d] via-[#0d3d5c] to-[#1a7a8a] border-t border-blue-600/30 shadow-2xl" data-id="element-181">
        <div className="max-w-6xl mx-auto px-6 py-4" data-id="element-182">
          <div className="mb-3" data-id="element-183">
            <div className="w-full h-1.5 bg-[#0a1e3d]/50 rounded-full overflow-hidden border border-blue-600/30" data-id="element-184">
              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-500 ease-out shadow-lg shadow-blue-500/50" style={{
              width: `${(currentStep + 1) / totalSteps * 100}%`
            }} data-id="element-185"></div>
            </div>
          </div>
          {isLastStep ? <div className="flex items-center justify-between gap-4" data-id="element-186">
              <div className="flex items-center gap-3" data-id="element-187">
                <button onClick={onCancel} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-300 bg-red-900/20 border border-red-500/30 rounded-lg hover:bg-red-900/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all" data-id="element-188">
                  <XIcon className="w-4 h-4" data-id="element-189" />
                  {isEditMode ? 'Cancel Edition' : 'Cancel'}
                </button>
                {!isEditMode && <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-200 hover:text-white disabled:opacity-60 disabled:cursor-not-allowed transition-all rounded-lg hover:bg-blue-900/30 hover:shadow-xl hover:shadow-blue-400/40" data-id="element-190">
                    <SaveIcon className="w-4 h-4" data-id="element-191" />
                    {isSavingDraft ? 'Guardando...' : 'Save as Draft'}
                  </button>}
              </div>
              <div className="flex items-center gap-6" data-id="element-192">
                <span className="text-sm font-bold text-blue-300 uppercase tracking-widest" data-id="element-193">
                  Paso {currentStep + 1} de {totalSteps}
                </span>
                <div className="flex items-center gap-3" data-id="element-194">
                  <button onClick={onBack} disabled={isSavingDraft} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-300 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all" data-id="element-195">
                    <ChevronLeftIcon className="w-4 h-4" data-id="element-196" />
                    Anterior
                  </button>
                  <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-blue-500 to-blue-400 rounded-lg hover:from-blue-600 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/40 hover:scale-105" data-id="element-197">
                    {isEditMode ? <>
                        <SaveIcon className="w-5 h-5" data-id="element-198" />
                        SAVE & FINISH
                      </> : <>
                        <CheckCircleIcon className="w-5 h-5" data-id="element-199" />
                        FINISH QUOTE
                      </>}
                  </button>
                </div>
              </div>
            </div> : <div className="flex items-center justify-between gap-4" data-id="element-200">
              <div className="flex items-center gap-3" data-id="element-201">
                <button onClick={onCancel} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-300 bg-red-900/20 border border-red-500/30 rounded-lg hover:bg-red-900/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all" data-id="element-202">
                  <XIcon className="w-4 h-4" data-id="element-203" />
                  {isEditMode ? 'Cancel Edition' : 'Cancel'}
                </button>
                <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-200 hover:text-white disabled:opacity-60 disabled:cursor-not-allowed transition-all rounded-lg hover:bg-blue-900/30 hover:shadow-xl hover:shadow-blue-400/40" data-id="element-204">
                  <SaveIcon className="w-4 h-4" data-id="element-205" />
                  {isSavingDraft ? 'Guardando...' : isEditMode ? 'Save and Finish' : 'Save as Draft'}
                </button>
              </div>
              <div className="flex items-center gap-6" data-id="element-206">
                <span className="text-sm font-bold text-blue-300 uppercase tracking-widest" data-id="element-207">
                  Paso {currentStep + 1} de {totalSteps}
                </span>
                <div className="flex items-center gap-3" data-id="element-208">
                  <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-300 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all" data-id="element-209">
                    <ChevronLeftIcon className="w-4 h-4" data-id="element-210" />
                    Anterior
                  </button>
                  <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-blue-500 to-blue-400 rounded-lg hover:from-blue-600 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/40 hover:scale-105" data-id="element-211">
                    Continuar
                    <ChevronRightIcon className="w-4 h-4" data-id="element-212" />
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>;
  }
  // Navy Gradient Design
  if (designVariant === 'navy-gradient') {
    return <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-900 via-blue-700 to-blue-600 border-t-2 border-blue-500/30 shadow-2xl" data-id="element-213">
        <div className="max-w-6xl mx-auto px-6 py-4" data-id="element-214">
          <div className="mb-3" data-id="element-215">
            <div className="flex items-center justify-between mb-3" data-id="element-216">
              <span className="text-xs font-bold text-blue-200 uppercase tracking-widest" data-id="element-217">
                Paso {currentStep + 1} de {totalSteps}
              </span>
              <span className="text-xs font-semibold text-blue-100 bg-blue-950/50 px-3 py-1 rounded-full border border-blue-500/30" data-id="element-218">
                {Math.round((currentStep + 1) / totalSteps * 100)}% Completado
              </span>
            </div>
            <div className="w-full h-2 bg-blue-950/50 rounded-full overflow-hidden border border-blue-500/30" data-id="element-219">
              <div className="h-full bg-gradient-to-r from-blue-400 to-blue-300 transition-all duration-500 ease-out shadow-lg shadow-blue-400/50" style={{
              width: `${(currentStep + 1) / totalSteps * 100}%`
            }} data-id="element-220"></div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4" data-id="element-221">
            <button onClick={onBack} disabled={currentStep === 0 || isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-blue-100 bg-blue-950/50 border border-blue-500/30 rounded-lg hover:bg-blue-950/70 disabled:opacity-40 disabled:cursor-not-allowed transition-all" data-id="element-222">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-223" />
              Anterior
            </button>
            <div className="flex items-center gap-3" data-id="element-224">
              <button onClick={onSaveDraft} disabled={isSavingDraft} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-blue-100 bg-blue-950/50 border border-blue-500/30 rounded-lg hover:bg-blue-950/70 disabled:opacity-60 disabled:cursor-not-allowed transition-all" data-id="element-225">
                {isSavingDraft ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-226" />
                    Guardando...
                  </> : <>
                    <SaveIcon className="w-4 h-4" data-id="element-227" />
                    Guardar
                  </>}
              </button>
              {isLastStep ? <button onClick={onFinalize} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-blue-950 bg-blue-400 rounded-lg hover:bg-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-400/30" data-id="element-228">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-229" />
                  Finalizar Cotización
                </button> : <button onClick={onNext} disabled={!canProceed || isSavingDraft} className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-blue-950 bg-blue-400 rounded-lg hover:bg-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-400/30" data-id="element-230">
                  Continuar
                  <ChevronRightIcon className="w-4 h-4" data-id="element-231" />
                </button>}
            </div>
          </div>
        </div>
      </div>;
  }
  return null;
}